/**
 * Zentrale Auth-Verwaltung für den optionalen Basic-Auth-Schutz (v0.3.8).
 *
 * Das Backend schützt (wenn BASIC_AUTH_USERS gesetzt ist) die gesamte API
 * per Basic-Auth. Das Frontend hält die Zugangsdaten als Base64-Token im
 * localStorage und schickt sie über einen Axios-Interceptor bzw. authFetch
 * bei jedem Request mit. Antwortet die API mit 401, wird ein Event
 * ausgelöst, auf das das AuthGate mit dem Login-Formular reagiert.
 */

import type { AxiosInstance } from 'axios'

const STORAGE_KEY = 'lage_auth'

/** Event, mit dem das AuthGate über eine abgelehnte Anfrage informiert wird. */
export const AUTH_REQUIRED_EVENT = 'lage-auth-required'

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function setAuthCredentials(user: string, password: string): void {
  // Base64 mit UTF-8-Unterstützung (Umlaute in Passwörtern)
  const token = btoa(String.fromCharCode(...new TextEncoder().encode(`${user}:${password}`)))
  localStorage.setItem(STORAGE_KEY, token)
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export function authHeader(): Record<string, string> {
  const token = getAuthToken()
  return token ? { Authorization: `Basic ${token}` } : {}
}

export function notifyAuthRequired(): void {
  window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT))
}

/**
 * Hängt Auth-Header + 401-Behandlung an eine Axios-Instanz.
 * Jede Service-Datei ruft das einmal nach axios.create() auf.
 */
export function attachAuth(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use((config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Basic ${token}`
    }
    return config
  })
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        notifyAuthRequired()
      }
      return Promise.reject(error)
    }
  )
  return instance
}

/**
 * Globaler fetch-Wrapper: hängt den Auth-Header an alle Requests an die
 * API-Base und löst bei 401 das Auth-Event aus. Deckt die fetch-basierten
 * Services (services/api.ts) und die direkten fetch-Aufrufe in den
 * Admin-Pages ab, ohne jede Stelle einzeln anzufassen.
 * Wird einmalig in main.tsx installiert (idempotent).
 */
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'
let fetchPatched = false

export function installFetchAuth(): void {
  if (fetchPatched) return
  fetchPatched = true
  const originalFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (!url.startsWith(API_BASE)) {
      return originalFetch(input, init)
    }
    const response = await originalFetch(input, {
      ...(init || {}),
      headers: { ...((init && init.headers) || {}), ...authHeader() },
    })
    if (response.status === 401) {
      notifyAuthRequired()
    }
    return response
  }
}
